if (!customElements.get('story-video')) {
  class StoryVideo extends HTMLElement {
    constructor() {
      super();
      this.autoplayPreview = this.dataset.autoplayPreview === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopMedia = window.matchMedia('(min-width: 768px)');
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.abortController = new AbortController();
      this.previewKey = null;
      this.fullVideoKey = null;
      this.inViewport = false;
      this.nearViewport = false;
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.previewMount = this.querySelector('[data-story-preview-media]');
      this.openButton = this.querySelector('[data-story-open]');
      this.modal = this.querySelector('[data-story-modal]');
      this.fullMediaMount = this.querySelector('[data-story-full-media]');
      const signal = this.abortController.signal;

      if (this.openButton && this.modal) {
        this.openButton.addEventListener('click', () => this.openFullVideo(), { signal });
      }

      document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), { signal });
      this.addMediaListener(this.reducedMotion, () => this.handlePreviewPreferenceChange());
      this.addMediaListener(this.desktopMedia, () => this.handleBreakpointChange());

      if ('IntersectionObserver' in window) {
        this.loadObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.nearViewport = entry.isIntersecting;
            if (this.nearViewport) this.preparePreview();
          });
        }, { rootMargin: '300px 0px', threshold: 0 });

        this.playObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.25;
            if (this.inViewport) this.playPreview();
            else this.pausePreview();
          });
        }, { threshold: [0, 0.25] });

        this.loadObserver.observe(this);
        this.playObserver.observe(this);
      } else {
        this.nearViewport = true;
        this.inViewport = true;
        this.preparePreview();
      }

      if (this.modal) {
        this.modalObserver = new MutationObserver(() => this.handleModalState());
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }
    }

    disconnectedCallback() {
      this.abortController.abort();
      if (this.loadObserver) this.loadObserver.disconnect();
      if (this.playObserver) this.playObserver.disconnect();
      if (this.modalObserver) this.modalObserver.disconnect();
      this.removeMediaListener(this.reducedMotion, this.reducedMotionHandler);
      this.removeMediaListener(this.desktopMedia, this.desktopMediaHandler);
    }

    addMediaListener(mediaQuery, handler) {
      const storedHandler = handler.bind(this);
      if (mediaQuery === this.reducedMotion) this.reducedMotionHandler = storedHandler;
      if (mediaQuery === this.desktopMedia) this.desktopMediaHandler = storedHandler;
      if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', storedHandler);
      else mediaQuery.addListener(storedHandler);
    }

    removeMediaListener(mediaQuery, handler) {
      if (!handler) return;
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', handler);
      else mediaQuery.removeListener(handler);
    }

    previewAllowed() {
      return this.autoplayPreview && !this.reducedMotion.matches && !this.saveData;
    }

    getPreviewTemplate() {
      const preferredKey = this.desktopMedia.matches ? 'desktop' : 'mobile';
      const fallbackKey = preferredKey === 'desktop' ? 'mobile' : 'desktop';
      const preferred = this.querySelector(`[data-story-preview-${preferredKey}]`);
      const fallback = this.querySelector(`[data-story-preview-${fallbackKey}]`);
      return preferred ? { key: preferredKey, template: preferred } : (fallback ? { key: fallbackKey, template: fallback } : null);
    }

    getFullVideoTemplate() {
      const preferredKey = this.desktopMedia.matches ? 'desktop' : 'mobile';
      const fallbackKey = preferredKey === 'desktop' ? 'mobile' : 'desktop';
      const preferred = this.querySelector(`[data-story-full-${preferredKey}]`);
      const fallback = this.querySelector(`[data-story-full-${fallbackKey}]`);
      return preferred ? { key: preferredKey, template: preferred } : (fallback ? { key: fallbackKey, template: fallback } : null);
    }

    preparePreview() {
      if (!this.previewAllowed() || !this.previewMount || !this.nearViewport) return;
      const source = this.getPreviewTemplate();
      if (!source || this.previewKey === source.key) return;

      this.pausePreview();
      this.classList.remove('is-preview-playing');
      this.previewMount.replaceChildren(source.template.content.cloneNode(true));
      this.previewVideo = this.previewMount.querySelector('video');
      this.previewKey = source.key;

      if (!this.previewVideo) return;
      this.previewVideo.muted = true;
      this.previewVideo.defaultMuted = true;
      this.previewVideo.loop = true;
      this.previewVideo.playsInline = true;
      this.previewVideo.setAttribute('aria-hidden', 'true');
      this.previewVideo.addEventListener('playing', () => this.classList.add('is-preview-playing'), {
        signal: this.abortController.signal
      });
      this.previewVideo.load();
      if (this.inViewport) this.playPreview();
    }

    async playPreview() {
      if (!this.previewAllowed() || document.hidden || (this.modal && this.modal.hasAttribute('open'))) return;
      if (!this.previewVideo) this.preparePreview();
      if (!this.previewVideo) return;
      try {
        await this.previewVideo.play();
      } catch (error) {
        this.classList.remove('is-preview-playing');
      }
    }

    pausePreview() {
      if (this.previewVideo && !this.previewVideo.paused) this.previewVideo.pause();
    }

    handleVisibilityChange() {
      if (document.hidden) this.pausePreview();
      else if (this.inViewport) this.playPreview();
    }

    handlePreviewPreferenceChange() {
      if (!this.previewAllowed()) {
        this.pausePreview();
        this.classList.remove('is-preview-playing');
        if (this.previewMount) this.previewMount.replaceChildren();
        this.previewVideo = null;
        this.previewKey = null;
      } else if (this.nearViewport) {
        this.preparePreview();
      }
    }

    handleBreakpointChange() {
      if (!this.previewAllowed() || !this.nearViewport) return;
      this.previewKey = null;
      this.preparePreview();
    }

    loadFullVideo() {
      if (!this.fullMediaMount) return null;
      const source = this.getFullVideoTemplate();
      if (!source) return null;
      if (this.fullVideo && this.fullVideoKey === source.key) return this.fullVideo;

      if (this.fullVideo) this.fullVideo.pause();
      this.fullMediaMount.replaceChildren(source.template.content.cloneNode(true));
      this.fullVideo = this.fullMediaMount.querySelector('video');
      this.fullVideoKey = source.key;
      const description = this.querySelector('[data-story-description]');

      if (this.fullVideo) {
        this.fullVideo.preload = 'auto';
        this.fullVideo.muted = false;
        if (description) {
          if (!description.id) description.id = `${this.id}-description`;
          this.fullVideo.setAttribute('aria-describedby', description.id);
        }
        this.fullVideo.load();
      }
      return this.fullVideo;
    }

    openFullVideo() {
      if (!this.modal) return;
      this.pausePreview();
      this.modal.open(this.openButton);
      const video = this.loadFullVideo();
      if (!video) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }

    handleModalState() {
      if (!this.modal || this.modal.hasAttribute('open')) return;
      if (this.fullVideo && !this.fullVideo.paused) this.fullVideo.pause();
      if (this.inViewport) this.playPreview();
    }
  }

  customElements.define('story-video', StoryVideo);
}
